import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Source } from '../../lib/models/Chat';
import './ChatComponents.css';
import { renderAsync } from 'docx-preview';
import { getFileExtension } from './getFileExtension';

// imports for fileViewer
import { Slide, Button, Dialog, AppBar, Toolbar, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TransitionProps } from '@mui/material/transitions';

// file viewer transition logic: slide to left
export const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
    ) {
    return <Slide direction="left" ref={ref} {...props} />;
});

// Citation File Viewer Contexts
interface urlContextType {
    url: string;
    setUrl: React.Dispatch<React.SetStateAction<string>>; 
}
export const urlContext = createContext<urlContextType>({
    url: '',           
    setUrl: () => {},  
});

interface highlightTextContextType {
    highlightText: string;
    setHighlightText: React.Dispatch<React.SetStateAction<string>>; 
}
export const highlightTextContext = createContext<highlightTextContextType>({
    highlightText: '',
    setHighlightText: () => {},
});

interface sourceContextType {
    source: Source | null;
    setSource: React.Dispatch<React.SetStateAction<Source | null>>;
}
export const sourceContext = createContext<sourceContextType>({
    source: null,
    setSource: () => {},
});

interface citationViewerOpenContextType {
    citationViewerOpen: boolean;
    setCitationViewerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
export const citationViewerOpenContext = createContext<citationViewerOpenContextType>({
    citationViewerOpen: false,
    setCitationViewerOpen: () => {},
});

const normalizeText = (text: string) =>
    text
        .replace(/[\u00A0\s]+/g, ' ')
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[\u2018\u2019]/g, "'")
        .trim();

// applies highlight on chunk text for each citation for docx
const applyHighlight = (root: HTMLElement, searchText?: string) => {
    // Remove existing highlights first
    const existingMarks = root.querySelectorAll('mark.citation-highlight');
    existingMarks.forEach((mark) => {
        const parent = mark.parentNode;
        while (mark.firstChild) {
            parent?.insertBefore(mark.firstChild, mark);
        }
        parent?.removeChild(mark);
    });
    root.normalize();

    if (!searchText) return;

    const normalizedSearch = normalizeText(searchText);

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let fullText = "";
    const nodes: { node: Text; start: number; end: number }[] = [];

    let currentNode: Node | null;
    while ((currentNode = walker.nextNode())) {
        const textNode = currentNode as Text;
        const start = fullText.length;
        const cleanedContent = (textNode.textContent || '')
            .replace(/[\u00A0\r\n\t]/g, ' ')
            .replace(/[\u201C\u201D]/g, '"')
            .replace(/[\u2018\u2019]/g, "'");
        fullText += cleanedContent;
        const end = fullText.length;
        nodes.push({ node: textNode, start, end });
    }

    const matchIndex = fullText.toLowerCase().indexOf(normalizedSearch.toLowerCase());

    if (matchIndex !== -1) {
        const matchEnd = matchIndex + normalizedSearch.length;
        const range = document.createRange();
        let startNode: Text | null = null;
        let endNode: Text | null = null;
        let startOffset = 0;
        let endOffset = 0;

        for (const { node, start, end } of nodes) {
            if (!startNode && matchIndex >= start && matchIndex < end) {
                startNode = node;
                startOffset = matchIndex - start;
            }
            if (matchEnd > start && matchEnd <= end) {
                endNode = node;
                endOffset = matchEnd - start;
                break;
            }
        }

        if (startNode && endNode) {
            try {
                range.setStart(startNode, startOffset);
                range.setEnd(endNode, endOffset);

                const mark = document.createElement('mark');
                mark.className = "citation-highlight";
                mark.style.backgroundColor = "rgba(255, 215, 0, 0.4)";
                mark.style.borderRadius = "2px";

                mark.appendChild(range.extractContents());
                range.insertNode(mark);

                mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } catch (err) {
                console.warn("Could not apply citation highlight range:", err);
            }
        }
    }
};

// DocxViewer: implements docx-viewer module to open up pdf/docx in tab
type ExtendedSource = Source & { title?: string; excerpt?: string };

export const DocxViewer: React.FC<{ url: string; highlightText?: string; source: Source | null }> = ({ url, highlightText, source }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const {citationViewerOpen, setCitationViewerOpen} = useContext(citationViewerOpenContext);

    // Canonical document URL
    const documentUrl = source?.presigned_url || url;

    // extract file type robustly
    const extension = getFileExtension(source, documentUrl);

    // Effect 1: Render DOCX document when dialog is open
    useEffect(() => {
        if (!citationViewerOpen || extension !== 'docx' || !documentUrl) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(false);

        let isMounted = true;
        const controller = new AbortController();

        if (containerRef.current) {
            containerRef.current.innerHTML = "";
        }

        // needed for docx
        fetch(documentUrl, { signal: controller.signal })
            .then(response => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.arrayBuffer();
            })
            .then(buffer => {
                if (containerRef.current && isMounted) {
                    return renderAsync(buffer, containerRef.current, undefined, {
                        className: "docx",
                        inWrapper: true,
                    });
                }
            })
            .then(() => {
                if (isMounted) setLoading(false);
            })
            .catch(err => {
                if (err.name === 'AbortError') return;
                console.error("Error rendering docx:", err);
                if (isMounted) {
                    setError(true);
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [documentUrl, extension, citationViewerOpen]);

    // Effect 2: Apply citation highlight when docx finishes loading or highlight/open state changes
    useEffect(() => {
        if (citationViewerOpen && extension === 'docx' && !loading && !error && containerRef.current) {
            applyHighlight(containerRef.current, highlightText || '');
        }
    }, [extension, loading, error, highlightText, citationViewerOpen]);

    const renderFallbackChunk = (titleText: string, messageText?: string) => {
        const extSource = source as ExtendedSource | null;
        const chunkText = highlightText || source?.chunk_content || extSource?.excerpt;
        const downloadUrl = documentUrl;

        return (
            <Box
                sx={{
                    p: { xs: 2, sm: 4 },
                    maxWidth: 800,
                    mx: 'auto',
                    width: '100%',
                    height: '100%',
                    overflowY: 'auto',
                    boxSizing: 'border-box',
                }}
            >
                <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {titleText}
                </Typography>
                {messageText && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {messageText}
                    </Typography>
                )}

                {chunkText && (
                    <Box
                        sx={{
                            p: 2.5,
                            bgcolor: 'background.paper',
                            borderLeft: '4px solid var(--color-accent, #1976d2)',
                            borderRadius: '6px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            mb: 3,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                                color: 'var(--color-accent, #1976d2)',
                                display: 'block',
                                mb: 1,
                            }}
                        >
                            Cited Passage
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: 'text.primary',
                                lineHeight: 1.6,
                            }}
                        >
                            {chunkText}
                        </Typography>
                    </Box>
                )}

                {downloadUrl && (
                    <Button
                        variant="contained"
                        component="a"
                        href={downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            bgcolor: 'var(--color-accent, #1976d2)',
                            '&:hover': { bgcolor: 'var(--color-accent-hover, #1565c0)' },
                            textTransform: 'none',
                        }}
                    >
                        Download Original Document
                    </Button>
                )}
            </Box>
        );
    };

    const fileType = () => {
        const pdfUrl = documentUrl;

        // pdf rendering in file viewer
        if (extension === 'pdf' && pdfUrl) {
            return (
                <Box
                    sx={(theme) => ({
                        bgcolor: '#f5f5f5',
                        height: '100%',
                        [theme.breakpoints.down('sm')]: {
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                        },
                    })}
                >
                    <Box
                        component="iframe"
                        src={pdfUrl}
                        sx={(theme) => ({
                            border: 'none',
                            width: '100%',
                            height: '100%',
                            [theme.breakpoints.down('sm')]: {
                                flex: 1,
                            },
                        })}
                        title="PDF Viewer"
                    />
                </Box>
            );
        }
    
        // docx rendering from url with highlighting
        if (extension === 'docx') {
            if (error || !documentUrl) {
                return renderFallbackChunk(
                    "Unable to Preview Word Document",
                    "The full document could not be loaded in the browser. You can read the cited passage below or download the document."
                );
            }

            return (
                <Box
                    sx={(theme) => ({
                        height: '100%',
                        overflow: 'auto',
                        bgcolor: '#f5f5f5',
                        position: 'relative',
                        [theme.breakpoints.down('sm')]: {
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                        },
                    })}
                >
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', position: 'absolute', width: '100%', zIndex: 1, bgcolor: 'rgba(255,255,255,0.7)' }}>
                            <CircularProgress />
                        </Box>
                    )}
    
                    <Box
                        ref={containerRef}
                        sx={(theme) => ({
                            height: 'calc(100vh - 48px)',
                            [theme.breakpoints.down('sm')]: {
                                height: '100%',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                WebkitOverflowScrolling: 'touch',
                                touchAction: 'pan-y',
                                paddingLeft: theme.spacing(1),
                                paddingRight: theme.spacing(1),
                                paddingTop: theme.spacing(2),
                                paddingBottom: theme.spacing(2),
                                boxSizing: 'border-box',
                            },
                        })}
                    />
                </Box> 
            );
        }
    
        // fallback case for other file formats or unrenderable documents
        const isSupportedFormat = extension === 'pdf' || extension === 'docx';
        return renderFallbackChunk(
            isSupportedFormat ? "Unable to Load Document" : "Document Preview Unavailable",
            isSupportedFormat
                ? "The full document could not be loaded in the browser. You can read the cited passage below or download the document."
                : "In-browser preview is not supported for this file format. You can read the cited passage below or download the document."
        );
    }

    const handleCitationViewerClose = () => setCitationViewerOpen(false);

    return (
        <Dialog
            fullScreen
            open={citationViewerOpen}
            onClose={handleCitationViewerClose}
            TransitionComponent={Transition}
            scroll="paper"
            PaperProps={{
                sx: (theme) => ({
                    [theme.breakpoints.down('sm')]: {
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100dvh',
                        maxHeight: '100dvh',
                        overflow: 'hidden',
                    },
                }),
            }}
            >
            <AppBar
                sx={(theme) => ({
                    position: 'relative',
                    bgcolor: 'var(--color-accent, #1976d2)',
                    [theme.breakpoints.down('sm')]: {
                        position: 'sticky',
                        top: 0,
                    },
                })}
            >
                <Toolbar
                    sx={(theme) => ({
                        [theme.breakpoints.down('sm')]: {
                            gap: 1,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            px: 1,
                        },
                    })}
                >
                    <IconButton edge="start" color="inherit" onClick={handleCitationViewerClose}>
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        sx={(theme) => ({
                            ml: 2,
                            flex: 1,
                            [theme.breakpoints.down('sm')]: {
                                ml: 0,
                                minWidth: 0,
                                width: 'calc(100% - 56px)',
                                fontSize: '1rem',
                                overflow: 'hidden',
                                textOverflow: 'clip',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                            },
                        })}
                        variant="h6"
                    >
                        {source?.name || (source as ExtendedSource | null)?.title || 'Document'}
                    </Typography>
                    {documentUrl && (
                        <Button
                            variant="outlined"
                            color="inherit"
                            component="a"
                            href={documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={(theme) => ({
                                [theme.breakpoints.down('sm')]: {
                                    ml: 'auto',
                                    width: '100%',
                                },
                            })}
                        >
                            Download
                        </Button>
                    )}
                </Toolbar>
            </AppBar>
            <Box
                sx={(theme) => ({
                    height: 'calc(100vh - 48px)',
                    [theme.breakpoints.down('sm')]: {
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        overflow: 'hidden',
                    },
                })}
            >
                {fileType()}
            </Box>
        </Dialog>
    );
};